import React, { useEffect, useRef } from "react";
import { Unity } from "react-unity-webgl";
import {
  dispatchUnityTouchCancel,
  useAndroidPinchGestureBridge,
} from "./androidTouchInput";
import { useLocalization } from "../../localization/LocalizationContext";

const INPUT_BLOCKED_EVENT_TYPES = [
  "click",
  "contextmenu",
  "dblclick",
  "keydown",
  "keypress",
  "keyup",
  "mousedown",
  "mousemove",
  "mouseup",
  "pointercancel",
  "pointerdown",
  "pointermove",
  "pointerup",
  "touchcancel",
  "touchend",
  "touchmove",
  "touchstart",
  "wheel",
];

const isNode = (value) =>
  typeof Node !== "undefined" && value instanceof Node;

const isTextEntryTarget = (target) => {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return (
    target.isContentEditable ||
    tagName === "input" ||
    tagName === "select" ||
    tagName === "textarea"
  );
};

const eventTargetsCanvas = (event, container) => {
  const target = event.target;
  const activeElement =
    typeof document === "undefined" ? null : document.activeElement;
  const targetIsInCanvas = isNode(target) && container.contains(target);

  if (!event.type.startsWith("key")) {
    return targetIsInCanvas;
  }

  return (
    targetIsInCanvas ||
    (isNode(activeElement) && container.contains(activeElement)) ||
    target === window ||
    target === document ||
    target === document.body
  );
};

const shouldBlockInputEvent = (event, container) => {
  if (!container || !eventTargetsCanvas(event, container)) {
    return false;
  }

  if (isTextEntryTarget(event.target) || isTextEntryTarget(document.activeElement)) {
    return false;
  }

  return !(event.type.startsWith("key") && (event.metaKey || event.ctrlKey));
};

const blockInputEvent = (event) => {
  if (event.cancelable) {
    event.preventDefault();
  }

  event.stopPropagation();
  event.stopImmediatePropagation?.();
};

export const getAndroidTouchContainerStyle = (androidTouchMode) =>
  androidTouchMode
    ? {
        touchAction: "none",
        overscrollBehavior: "none",
      }
    : {};

export const getAndroidTouchCanvasStyle = (androidTouchMode) =>
  androidTouchMode ? { touchAction: "none" } : {};

const UnityCanvas = ({
  androidTouchMode = false,
  unityProvider,
  cursorStyle,
  inputBlocked = false,
  isLoaded = false,
  loadingProgression = 0,
  onApplyPinchGesture,
  playerOffset,
}) => {
  const { t } = useLocalization();
  const containerRef = useRef(null);
  const androidTouchBridgeEnabled = Boolean(
    androidTouchMode && isLoaded && !inputBlocked && onApplyPinchGesture,
  );
  const loadingPercent = Math.min(
    100,
    Math.max(0, Math.round((Number(loadingProgression) || 0) * 100)),
  );

  useAndroidPinchGestureBridge({
    cancelRawTouchInput: (event) =>
      dispatchUnityTouchCancel(containerRef.current, event),
    containerRef,
    enabled: androidTouchBridgeEnabled,
    sendGesture: onApplyPinchGesture,
  });

  useEffect(() => {
    if (!inputBlocked || typeof window === "undefined") {
      return undefined;
    }

    const handleInputEvent = (event) => {
      if (shouldBlockInputEvent(event, containerRef.current)) {
        blockInputEvent(event);
      }
    };

    INPUT_BLOCKED_EVENT_TYPES.forEach((eventType) => {
      window.addEventListener(eventType, handleInputEvent, true);
    });

    return () => {
      INPUT_BLOCKED_EVENT_TYPES.forEach((eventType) => {
        window.removeEventListener(eventType, handleInputEvent, true);
      });
    };
  }, [inputBlocked]);

  return (
    <div
      ref={containerRef}
      className="unity-canvas-container"
      data-testid="unity-canvas-container"
      style={{
        position: "absolute",
        top: 0,
        left: `${playerOffset}px`,
        width: `calc(100% - ${playerOffset}px)`,
        height: "100%",
        ...getAndroidTouchContainerStyle(androidTouchMode),
      }}
    >
      <Unity
        unityProvider={unityProvider}
        style={{
          width: "100%",
          height: "100%",
          cursor: cursorStyle,
          mt: "50px",
          pointerEvents: inputBlocked ? "none" : "auto",
          ...getAndroidTouchCanvasStyle(androidTouchMode),
        }}
        devicePixelRatio={window.devicePixelRatio}
        width="100%"
        height="100%"
      />
      {!isLoaded && (
        <div
          aria-live="polite"
          data-testid="unity-startup-overlay"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            color: "#fff",
            textAlign: "center",
            pointerEvents: "none",
            background:
              "radial-gradient(circle at top left, rgba(39, 94, 107, 0.25) 0%, rgba(9, 15, 23, 0) 36%), linear-gradient(180deg, #060b10 0%, #0c141d 100%)",
          }}
        >
          <div style={{ width: "min(100%, 420px)" }}>
            <div
              style={{
                marginBottom: "10px",
                fontSize: "1.45rem",
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {t("loading.player")}
            </div>
            <div
              style={{
                marginBottom: "18px",
                color: "rgba(255, 255, 255, 0.72)",
                fontSize: "0.98rem",
                lineHeight: 1.45,
              }}
            >
              {t("loading.starting")}
            </div>
            <div
              style={{
                height: "6px",
                overflow: "hidden",
                borderRadius: "999px",
                background: "rgba(255, 255, 255, 0.16)",
              }}
            >
              <div
                data-testid="unity-startup-progress"
                style={{
                  width: `${loadingPercent}%`,
                  height: "100%",
                  borderRadius: "inherit",
                  background:
                    "linear-gradient(90deg, #6ee7f9 0%, #8cf7d2 100%)",
                  transition: "width 240ms ease",
                }}
              />
            </div>
          </div>
        </div>
      )}
      {inputBlocked && (
        <div
          aria-hidden="true"
          data-testid="unity-input-blocker"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            cursor: "progress",
            pointerEvents: "auto",
            touchAction: "none",
          }}
        />
      )}
    </div>
  );
};

export default UnityCanvas;
